// =======================================
// DATA TYPES
// =======================================

/**
 * A linked project or stage, as denormalized by ncr_list_view.
 */
export interface NcrProjectRef {
  id: number;
  project_id: number | null;
  stage_id: number | null;
  entity_type: 'project' | 'stage';
  job_number_display: string; // "1234" or "1234.A"
  description: string | null;
}

/**
 * Non-conformance record as returned by get_filtered_ncrs.
 */
export interface Ncr {
  id: number;
  reference_seq: number;
  reference: string; // "NCR-0001"
  type: 'Internal' | 'External';
  title: string | null;
  project_id: number | null;
  stage_id: number | null;
  project: NcrProjectRef | null;
  raised_by: {
    id: string;
    first_name: string;
    last_name: string;
  } | null;
  client_id: number | null;
  client: { id: number; company_name: string } | null;
  contact_id: number | null;
  contact: { id: number; first_name: string; last_name: string } | null;
  date_raised: string;
  classification: NcrClassification | null;
  severity: 'Minor' | 'Major' | 'Critical' | null;
  description: string | null;
  requirement_not_met: string | null;
  impact: string | null;
  external_reported_details: string | null;
  internal_investigation: string | null;
  action_to_be_taken: string | null;
  status: 'Open' | 'In Progress' | 'Resolved' | 'Closed';
  target_close_out_date: string | null;
  actual_close_out_date: string | null;
  created_at: string;
  updated_at?: string;
}

// =======================================
// COLUMN CONFIG
// =======================================

export interface NcrColumnConfig {
  header: string;
  /** Renderer key used by index.tsx */
  component?: 'TypeBadge' | 'StatusBadge' | 'SeverityBadge' | 'ClassificationBadge' | 'DateColumn' | 'ProjectColumn' | 'TitleColumn' | 'RaisedByColumn';
  sortable?: boolean;
}

/**
 * Table column definitions for the NCR register (in display order).
 */
export const ncrColumns: Record<string, NcrColumnConfig> = {
  reference: { header: 'NCR #', sortable: true },
  type: { header: 'Type', component: 'TypeBadge' },
  date_raised: { header: 'Date Raised', component: 'DateColumn', sortable: true },
  title: { header: 'Title', component: 'TitleColumn' },
  project: { header: 'Project / Stage', component: 'ProjectColumn' },
  classification: { header: 'Classification', component: 'ClassificationBadge' },
  severity: { header: 'Severity', component: 'SeverityBadge' },
  status: { header: 'Status', component: 'StatusBadge', sortable: true },
  target_close_out_date: { header: 'Close Out', component: 'DateColumn', sortable: true },
  raised_by: { header: 'Raised By', component: 'RaisedByColumn' },
};

/**
 * Per-status action counts returned in the get_filtered_ncrs `summary`.
 */
export interface NcrSummary {
  open_count: number;
  in_progress_count: number;
  resolved_count: number;
  closed_count: number;
  total_count: number;
}

// =======================================
// CONSTANTS AND OPTIONS
// =======================================

export const NCR_TYPE_OPTIONS = ['Internal', 'External'] as const;
export type NcrType = typeof NCR_TYPE_OPTIONS[number];

export const NCR_STATUS_OPTIONS = ['Open', 'In Progress', 'Resolved', 'Closed'] as const;
export type NcrStatus = typeof NCR_STATUS_OPTIONS[number];

export const NCR_SEVERITY_OPTIONS = ['Minor', 'Major', 'Critical'] as const;
export type NcrSeverity = typeof NCR_SEVERITY_OPTIONS[number];

/** Nonconformance type (PDF Section 2). Backed by the ncr_classification DB enum. */
export const NCR_CLASSIFICATION_OPTIONS = [
  'Documentation error',
  'Construction issue - design',
  'Construction issue - builder',
  'Process nonconformance',
  'Other',
] as const;
export type NcrClassification = typeof NCR_CLASSIFICATION_OPTIONS[number];

export const DEFAULT_PAGE_SIZE = 50;
export const SEARCH_DEBOUNCE_DELAY = 300;

// =======================================
// SHARED MODAL FORM STATE
// =======================================

/**
 * Editable form state shared by AddNcrModal and NcrDetailModal (via NcrFormFields).
 * project_id/stage_id/client_id/contact_id are stored as strings for form inputs
 * and parsed on submit.
 */
export interface NcrFormData {
  type: 'Internal' | 'External';
  title: string;
  project_id: string;
  stage_id: string | null;
  raised_by: string;
  client_id: string;
  contact_id: string;
  date_raised: string;
  classification: string;
  severity: string;
  description: string;
  requirement_not_met: string;
  impact: string;
  external_reported_details: string;
  internal_investigation: string;
  action_to_be_taken: string;
  status: string;
  target_close_out_date: string;
  actual_close_out_date: string;
}

export const emptyNcrForm = (): NcrFormData => ({
  type: 'Internal',
  title: '',
  project_id: '',
  stage_id: null,
  raised_by: '',
  client_id: '',
  contact_id: '',
  date_raised: new Date().toISOString().split('T')[0],
  classification: '',
  severity: '',
  description: '',
  requirement_not_met: '',
  impact: '',
  external_reported_details: '',
  internal_investigation: '',
  action_to_be_taken: '',
  status: 'Open',
  target_close_out_date: '',
  actual_close_out_date: '',
});

/** Inline field-level validation errors (shared by Add + Detail modals). */
export interface NcrFormErrors {
  type?: string;
  title?: string;
  raised_by?: string;
  date_raised?: string;
  client_id?: string;
  contact_id?: string;
  classification?: string;
  severity?: string;
  description?: string;
  requirement_not_met?: string;
  impact?: string;
  external_reported_details?: string;
  internal_investigation?: string;
}

/**
 * Required-field validation, enforced identically on create and edit.
 * Project / Stage is intentionally NOT required — process nonconformances may not
 * relate to a project.
 */
export function validateNcr(f: NcrFormData): NcrFormErrors {
  const e: NcrFormErrors = {};
  if (!f.title.trim()) e.title = 'Title is required';
  if (!f.raised_by) e.raised_by = 'Raised by is required';
  if (!f.date_raised) e.date_raised = 'Date raised is required';
  if (!f.classification) e.classification = 'NCR type is required';
  if (!f.severity) e.severity = 'Severity is required';
  if (!f.requirement_not_met.trim()) e.requirement_not_met = 'Requirement not met is required';
  if (!f.impact.trim()) e.impact = 'Impact is required';
  if (f.type === 'External') {
    if (!f.client_id) e.client_id = 'Issuing client is required';
    if (!f.contact_id) e.contact_id = 'Contact is required';
    if (!f.external_reported_details.trim()) e.external_reported_details = 'This field is required';
    if (!f.internal_investigation.trim()) e.internal_investigation = 'This field is required';
  } else {
    if (!f.description.trim()) e.description = 'Description is required';
  }
  return e;
}

// --- Picker reducers (shared so create + edit map selections identically) ---

/** Project/stage selection → project_id (parent project) + stage_id. */
export function withProjectSelection(
  form: NcrFormData,
  projectId: string | number | null,
  project?: { entity_type?: string; parent_project_id?: number } | null,
): NcrFormData {
  if (projectId && project) {
    if (project.entity_type === 'stage') {
      return { ...form, project_id: project.parent_project_id?.toString() || '', stage_id: projectId.toString() };
    }
    return { ...form, project_id: projectId.toString(), stage_id: null };
  }
  return { ...form, project_id: '', stage_id: null };
}

/** Issuing client selection → client_id (also clears the dependent contact). */
export function withClientSelection(form: NcrFormData, clientId: string | number | null): NcrFormData {
  return { ...form, client_id: clientId && clientId !== 'none' ? clientId.toString() : '', contact_id: '' };
}

/** Issuing contact selection → contact_id. */
export function withContactSelection(form: NcrFormData, contactId: string | number | null): NcrFormData {
  return { ...form, contact_id: contactId ? contactId.toString() : '' };
}

// --- Persistence mapping (single source of truth for form → DB columns) ---

const idOrNull = (s: string | null): number | null => (s ? parseInt(s, 10) : null);
const textOrNull = (s: string): string | null => (s.trim() ? s.trim() : null);

/** Normalised column values — used directly as the edit UPDATE payload. */
export function ncrColumnValues(f: NcrFormData) {
  return {
    type: f.type,
    title: textOrNull(f.title),
    project_id: idOrNull(f.project_id),
    stage_id: idOrNull(f.stage_id),
    raised_by: f.raised_by || null,
    client_id: idOrNull(f.client_id),
    contact_id: idOrNull(f.contact_id),
    date_raised: f.date_raised || null,
    classification: f.classification || null,
    severity: f.severity || null,
    description: textOrNull(f.description),
    requirement_not_met: textOrNull(f.requirement_not_met),
    impact: textOrNull(f.impact),
    external_reported_details: textOrNull(f.external_reported_details),
    internal_investigation: textOrNull(f.internal_investigation),
    action_to_be_taken: textOrNull(f.action_to_be_taken),
    status: f.status,
    target_close_out_date: f.target_close_out_date || null,
    actual_close_out_date: f.actual_close_out_date || null,
  };
}

/** Params for the create_non_conformance RPC (create path). */
export function buildCreateParams(f: NcrFormData) {
  const v = ncrColumnValues(f);
  return {
    p_type: v.type,
    p_title: v.title,
    p_project_id: v.project_id,
    p_stage_id: v.stage_id,
    p_raised_by: v.raised_by,
    p_client_id: v.client_id,
    p_contact_id: v.contact_id,
    p_date_raised: f.date_raised,
    p_classification: v.classification,
    p_severity: v.severity,
    p_description: v.description,
    p_requirement_not_met: v.requirement_not_met,
    p_impact: v.impact,
    p_external_reported_details: v.external_reported_details,
    p_internal_investigation: v.internal_investigation,
    p_action_to_be_taken: v.action_to_be_taken,
    p_status: v.status || 'Open',
    p_target_close_out_date: v.target_close_out_date,
  };
}
